export function receiptStoreName(text) {
  const stores = [
    ["Shoppers Drug Mart", /\bshoppers?\s*(?:drug\s*mart)?\b/i],
    ["Walmart", /\bwal[\s-]*mart\b/i],
    ["Real Canadian Superstore", /\b(?:real\s*canadian\s*)?super[\s-]*store\b/i],
    ["FreshCo", /\bfresh[\s.-]*co\b/i],
    ["Amazon", /\bamazon(?:\.(?:ca|com))?\b/i],
    ["Costco", /\bcostco\b/i]
  ];
  const matches = stores.map(([name, pattern]) => ({ name, index: text.search(pattern) })).filter(store => store.index >= 0).sort((a, b) => a.index - b.index);
  return matches[0]?.name || text.split(/\r?\n/).map(line => line.trim()).find(Boolean) || "";
}

export function receiptDate(text) {
  const month = "(?:January|February|March|April|May|June|July|August|September|October|November|December|Jan|Feb|Mar|Apr|Jun|Jul|Aug|Sep|Sept|Oct|Nov|Dec)";
  const written = new RegExp(`\\b(?:${month}\\.?[\\s/-]+\\d{1,2}(?:st|nd|rd|th)?(?:,?[\\s/-]*\\d{4})?|\\d{1,2}(?:st|nd|rd|th)?[\\s/-]+${month}\\.?(?:,?[\\s/-]*\\d{4})?)\\b`, "i");
  return text.match(written)?.[0] || text.match(/\b(?:\d{4}[-/]\d{1,2}[-/]\d{1,2}|\d{1,2}[-/]\d{1,2}[-/]\d{2,4})\b/)?.[0] || "";
}

export function receiptFields(text, filename) {
  const lines = text.split(/\r?\n/).map(s => s.trim()).filter(Boolean);
  const amount = pattern => {
    const index = lines.findIndex(line => pattern.test(line) && !( /\bsub[\s-]*t[o0]tal\b/i.test(line) && !pattern.source.includes("sub") ) && !/\b(?:registration|reg|number|points|balance)\b|\d{9}\s*RT/i.test(line));
    if (index < 0) return "";
    const afterLabel = lines[index].slice(lines[index].match(pattern).index + lines[index].match(pattern)[0].length);
    const read = value => {
      const cleaned = value.replace(/\d+(?:[.,]\d+)?\s*%/g, "")
        .replace(/([0-9])\s*[.,]\s*([0-9]{2})\b/g, "$1.$2");
      const matches = cleaned.match(/\d[\d,]*\.\d{2}\b/g);
      return matches?.[0]?.replace(/,/g, "") || "";
    };
    const sameLine = read(afterLabel);
    if (sameLine) return sameLine;
    const next = lines[index + 1] || "";
    return /^[\s$£€:=-]*\d[\d ,.]*$/.test(next) ? read(next) : "";
  };
  const labelled = pattern => lines.find(line => pattern.test(line))?.replace(pattern, "").trim() || "";
  const addressIndex = lines.findIndex(line => /^\d{1,6}\s+.*\b(?:street|st|avenue|ave|road|rd|drive|dr|blvd|boulevard|way|highway|hwy)\b/i.test(line));
  const supplierAddress = addressIndex >= 0 ? lines.slice(addressIndex, addressIndex + 3).filter(line => !/\b(?:total|gst|pst|tel|phone)\b|\d{4}[-/]\d{2}/i.test(line)).join(" · ") : "";
  const gstNumber = text.match(/\b\d{9}\s*RT\s*\d{4}\b/i)?.[0]?.replace(/\s/g, "") || labelled(/^(?:GST\/?HST|GST|HST)\s*(?:registration|reg\.?|number|no\.?|#)\s*[:#]?\s*/i);
  const itemLines = lines.filter(line => /\d[\d,]*\.\d{2}/.test(line) && !/\b(?:total|subtotal|sub total|gst|hst|pst|tax|visa|mastercard|debit|cash|change|balance|payment|amount|tender|discount|savings|tip)\b/i.test(line)).join("\n");
  const extras = {
    supplierAddress, buyer: labelled(/^(?:bill(?:ed)? to|buyer|customer name)\s*[:#]\s*/i),
    buyerAddress: labelled(/^(?:buyer address|billing address)\s*[:#]\s*/i), gstNumber,
    receiptNumber: labelled(/^(?:receipt|invoice|transaction)\s*(?:number|no\.?|#)\s*[:#]?\s*/i),
    paymentMethod: text.match(/\b(?:VISA|MASTERCARD|AMEX|AMERICAN EXPRESS|INTERAC|DEBIT|CASH)\b/i)?.[0]?.toUpperCase() || "",
    items: itemLines, businessPurpose: "", expenseCategory: ""
  };
  const record = { id: `${Date.now()}-${Math.random().toString(36).slice(2)}`, filename, ...extras, merchant: receiptStoreName(text), date: receiptDate(text), subtotal: amount(/\bsub[\s-]*t[o0]tal\b/i), gst: amount(/\b(?:G[S5][T7I]|HST)\b/i), pst: amount(/\bP[S5][T7I]\b/i), total: amount(/\b(?:t[o0]tal\s+amount|grand\s+t[o0]tal|t[o0]tal|amount\s+due)\b/i), currency: /\bUSD\b|US\$/i.test(text) ? "USD" : "CAD", notes: "", text, reviewed: false };
  const paymentLine = lines.find(line => /\b(?:tendered|amount paid|paid by)\b/i.test(line));
  const payment = paymentLine?.match(/\d[\d,]*[.,]\d{2}\b/g)?.pop()?.replace(/,/g, ".");
  if (record.total && payment && Number(record.total) !== Number(payment)) {
    record.scanWarning = `The total (${record.total}) differs from the payment amount (${payment}). Check the original receipt and enter the correct total.`;
    record.total = "";
  }
  return record;

}

export async function receiptWorkbook(records) {
  const { default: ExcelJS } = await import("exceljs");
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet("Receipt records", { views: [{ state: "frozen", ySplit: 1 }] });
  sheet.columns = [
    { header: "Store name", key: "merchant", width: 30 },
    { header: "Date (as printed)", key: "date", width: 26 },
    { header: "Subtotal", key: "subtotal", width: 18 },
    { header: "GST", key: "gst", width: 18 },
    { header: "PST", key: "pst", width: 18 },
    { header: "Total amount", key: "total", width: 18 }
  ];
  records.forEach(record => {
    const row = { merchant: record.merchant, date: record.date };
    ["subtotal", "gst", "pst", "total"].forEach(key => { row[key] = record[key] == null || record[key] === "" ? null : Number(record[key]); });
    sheet.addRow(row);
  });
  ["subtotal", "gst", "pst", "total"].forEach(key => { sheet.getColumn(key).numFmt = '#,##0.00'; });
  sheet.autoFilter = { from: "A1", to: `F${records.length + 1}` };
  for (const page of [sheet]) {
    page.getRow(1).font = { bold: true, color: { argb: "FFFFFFFF" } };
    page.getRow(1).fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF202938" } };
    page.getRow(1).height = 24;
  }
  return workbook.xlsx.writeBuffer();
}
